import GpClientApiBase, { IGpExecuteResponse } from '../DelineateWatershedGpApi/GpApiClientBase';
import { FeatureSet, FeatureLayer, RasterData, MapImageLayer } from 'cd-eft/utils/esriTypes';
import { appConfig } from 'cd-eft/config/AppConfig';
import { IMapImageLayerConfig } from '../DelineateWatershedGpApi/DelineateWatershedGpApi';

const __apiConfig = appConfig.demSurfaceGpApiConfig;

export default class DemSurfaceGpApi extends GpClientApiBase {
  private readonly __baseUrl: string;
  private readonly __demSurfaceUpload: string;
  private readonly __loadSessionEfh2Url: string;
  private readonly __exportSessionEfh2Url: string;
  private readonly __loadDEMFromSession: string;

  constructor() {
    super(__apiConfig.timeout);
    this.__baseUrl = __apiConfig.baseUrl;
    this.__demSurfaceUpload = new URL(__apiConfig.demUploadPath, this.__baseUrl).toString();
    this.__loadDEMFromSession = new URL(__apiConfig.loadDEMFromSession, this.__baseUrl).toString();
    this.__loadSessionEfh2Url = new URL(__apiConfig.loadSessionEFH2Path, this.__baseUrl).toString();
    this.__exportSessionEfh2Url = new URL(__apiConfig.exportSessionEFH2Path, this.__baseUrl).toString();
  }


  async exportSessionEfh2(
    gpSessionId: string,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<string> {
    const url = this.__exportSessionEfh2Url;
    const params: any = { input_sessionId: gpSessionId };
    const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(url, params, waitOptions);
    const dataFile: any = await gpResponse.getResultDataValue('output_zipfile');
    const result: string = dataFile.url;
    return result;
  }

  async GenMapServiceFromDem(
    requestParams: IDemSurfaceUploadRequestParams,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<Readonly<IDemSurfaceUploadResponse>> {
    const serviceUrl = this.__demSurfaceUpload;
    // upload the file
    let in_dem_file_info: any;
    if (requestParams.in_dem) {
      if (Object.prototype.toString.call(requestParams.in_dem) === '[object File]') {
        in_dem_file_info = await this.__uploadFile(serviceUrl, requestParams.in_dem as File);
      }
    }
    try {
      const params: IDemSurfaceUploadRequestParams = { ...requestParams };

      if (in_dem_file_info) {
        params.in_dem = in_dem_file_info;
        //NOTE: in_view_dem = true, returning the result_dem RasterData is required if new file is uploaded,
        //otherwise FE will not have server reference for future calls and file will need to be uploaded repeatedy, very slow
      } else {
        params.in_dem = this.__getRasterDataProperties(requestParams.in_dem as RasterData);
      }
      const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(serviceUrl, params, waitOptions);

      const result: IDemSurfaceUploadResponse = {
        out_result_manifest: [],
      };

      const rasterResultNames = ['result_dem', 'result_sessionId'];
      for (const resultName of rasterResultNames) {
        try {
          result[resultName] = await gpResponse.getResultDataValue<RasterData>(resultName);
          result.out_result_manifest.push(resultName);
        } catch (error) {
          //nothing to see here, errors are expected if value does not exist on server
        }
      }

      // const featureResultNames = [
      //   'result_dem_boundary',
      // ];
      // for (const resultName of featureResultNames) {
      //   try {
      //     const featureSet = await gpResponse.getResultDataValue<FeatureSet>(resultName);
      //     if (featureSet.features.length > 0) {
      //       result[resultName] = featureSet;
      //       result.out_result_manifest.push(resultName);
      //     }
      //   } catch (error) {
      //     //nothing to see here, errors are expected if value does not exist on server
      //   }
      // }

      const mapImageLayerResultNames = [
        'result_dem',
      ];
      for (const resultName of mapImageLayerResultNames) {
        if (result.out_result_manifest.includes(resultName)) {
          const mapImageLayer = await gpResponse.getResultMapImageLayer();
          mapImageLayer.id = resultName;
          const resultLayerName = resultName + '_layer';
          if (__resultLayersConfig && __resultLayersConfig[resultLayerName]) {
            mapImageLayer.title = __resultLayersConfig[resultLayerName].title;
            mapImageLayer.visible = __resultLayersConfig[resultLayerName].visible;
            mapImageLayer.listMode = __resultLayersConfig[resultLayerName].listMode;
            mapImageLayer.opacity = __resultLayersConfig[resultLayerName].opacity || 1.0;
          } else {
            mapImageLayer.title = resultName;
            mapImageLayer.visible = true;
            mapImageLayer.listMode = 'hide-children';
          }
          //if (resultName === 'result_dem') mapImageLayer.opacity = 0.3; // initially load the 'result_dem' layer with 30% opacity//
          mapImageLayer.when(
            () => {
              mapImageLayer.sublayers.forEach((sublayer) => {
                if (sublayer.title === resultName) {
                  sublayer.visible = true;
                } else {
                  sublayer.visible = false;
                }
              });
            },
            (error: any) => {
              console.error(error);
              //TODO: handle error
              //send to global error handler?
              //let client handle it, requires explicit?
            }
          );
          // const resultLayerName = resultName.replace('output_', 'layer_');

          result[resultLayerName] = mapImageLayer;
        }
      }

      const featureLayerResultNames = [
        'result_dem_boundary',
      ];
      for (const resultName of featureLayerResultNames) {
        if (result.out_result_manifest.includes(resultName)) {
          // const resultLayerName = resultName.replace('output_', 'layer_');
          const resultLayerName = resultName + '_layer';
          const featureSet: FeatureSet = result[resultName];
          const objectIdField = featureSet.fields.filter((field) => {
            return field.type === 'oid';
          })[0].name;
          let featureLayer: FeatureLayer;
          if (__resultLayersConfig[resultLayerName]) {
            const props = { ...__resultLayersConfig[resultLayerName] };
            props.source = featureSet.features;
            props.objectIdField = objectIdField;
            featureLayer = new FeatureLayer(props);
            featureLayer.id = resultName;
          } else {
            featureLayer = new FeatureLayer({
              id: resultName,
              source: featureSet.features,
              title: resultName,
              visible: true,
              objectIdField: objectIdField,
              renderer: {
                type: 'simple',
                symbol: {
                  type: 'simple-fill',
                  style: 'none',
                  color: [0, 0, 0, 0],
                  outline: {
                    type: 'simple-line',
                    style: 'solid',
                    color: 'green',
                    width: '2px',
                  } as unknown, //SimpleLineSymbol
                } as unknown, //SimpleFillSymbol
              } as any, //SimpleRenderer
            });
          }
          result[resultLayerName] = featureLayer;
        }
      }

      return result;
    }
    catch (err) {
      // console.log((err as any).messages.map(m => m.description));
      throw err;
    }
    finally {
    }
  }

  async LoadDEMFromSession(
    requestParams: IDemFromSessionRequestParams,
    waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions
  ): Promise<MapImageLayer> {
    const serviceUrl = this.__loadDEMFromSession
    const params: IDemFromSessionRequestParams = {
      input_server_folder: requestParams.input_server_folder,
      input_session: requestParams.input_session
    };
    const gpResponse = await this.__invokeGpSubmitAndWaitForCompletion(serviceUrl, params, waitOptions);
    const demLayer = await gpResponse.getResultMapImageLayer();
    return demLayer;
  }
}
export interface IDemFromSessionRequestParams {
  input_server_folder?: string;
  input_session: string;
}

export interface IDemSurfaceUploadRequestParams {
  in_server_folder?: string;
  in_dem?: RasterData | File;
  in_dem_elevation_units?: string;
}

export interface IDemSurfaceUploadResponse {
  out_result_manifest: string[];
  result_dem?: RasterData;
  result_dem_boundary?: FeatureSet;
  result_sessionId?: string;
}


export interface IDelineateWatershedLayersConfig {
  result_dem_layer?: IMapImageLayerConfig;
}




const __resultLayersConfig: IDelineateWatershedLayersConfig = {
  result_dem_layer: { title: 'DEM (original)', visible: true, listMode: 'hide-children', opacity: 0.5 },

};
